import { Component } from "react";
import PropTypes from "prop-types";
import { Card, Row, Col, Form, Button, Alert, Table, Spinner, Badge } from "react-bootstrap";
import { FaUserPlus, FaCheck, FaTimes, FaFilePdf, FaExclamationTriangle } from "react-icons/fa";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import api from "../../services/api";
import { IMaskInput } from "react-imask";
import { withRouter } from "../../common/with-router";
import { getCurrentUser } from "../../common/utils";
import { formataDataHoraExibicao } from "../../common/utils";

const MySwal = withReactContent(Swal);

const CONSELHOS = ["CRN", "COREN", "CREFITO", "CRP", "CRF", "CREF", "Outro"];
const UFS = ["AC","AL","AM","AP","BA","CE","DF","ES","GO","MA","MG","MS","MT","PA","PB",
             "PE","PI","PR","RJ","RN","RO","RR","RS","SC","SE","SP","TO"];

/**
 * Indicação de Profissional de Saúde, na visão do médico.
 *
 * O médico indica quem vai acompanhar seus pacientes durante o teste e assina a
 * declaração. Antes de uma nova indicação, o documento manda revisar as anteriores —
 * é o passo de "manter ou encerrar" que abre a tela quando já existe indicação.
 *
 * Indicação nunca é excluída: encerrar muda o status e o registro continua no histórico.
 */
class IndicacoesProfissionais extends Component {
    constructor(props) {
        super(props);
        this.state = {
            carregando: true,
            erro: "",
            programa: null,
            indicacoes: [],
            // revisão das indicações anteriores, exigida antes de indicar de novo
            revisando: false,
            mostrarFormulario: false,
            salvando: false,
            form: this.formVazio(),
            erros: {},
        };
    }

    formVazio = () => ({
        nome_indicado: "",
        conselho: "",
        registro: "",
        registro_uf: "",
        telefone: "",
        email: "",
        autoriza_contato: null,
        assino: false,
    });

    componentDidMount() {
        this.carregar();
    }

    programaId = () => getCurrentUser()?.["ambiente_programa_id"] || "";

    carregar = async () => {
        const programaId = this.programaId();
        if (!programaId) {
            this.setState({ carregando: false, erro: "Nenhum programa em contexto." });
            return;
        }

        try {
            const { data } = await api.get(`/pessoas/indicacoes-profissionais/minhas/?programa_id=${programaId}`);
            const indicacoes = data.indicacoes || [];
            const ativas = indicacoes.filter((i) => i.status === "ativa");
            this.setState({
                carregando: false,
                programa: data.programa,
                indicacoes,
                // com indicação ativa, a tela abre pela revisão; sem nenhuma, vai direto ao formulário
                revisando: ativas.length > 0,
                mostrarFormulario: ativas.length === 0,
            });
        } catch (error) {
            const detalhe = error.response?.data?.detail;
            this.setState({
                carregando: false,
                erro: detalhe || "Não foi possível carregar suas indicações. Tente novamente.",
            });
        }
    };

    /** IMaskInput devolve o valor, nao o evento -- mesmo padrao do cadastro de médico. */
    handleMaskAccept = (campo) => (valor) => {
        this.setState((prev) => ({
            form: { ...prev.form, [campo]: valor },
            erros: { ...prev.erros, [campo]: "" },
        }));
    };

    handleChange = (e) => {
        const { name, value } = e.target;
        this.setState((prev) => ({ form: { ...prev.form, [name]: value }, erros: { ...prev.erros, [name]: "" } }));
    };

    /** Encerra a indicação sem apagá-la: o registro fica no histórico. */
    encerrarIndicacao = async (indicacao) => {
        const escolha = await MySwal.fire({
            title: "Encerrar indicação?",
            html: `Esta indicação será encerrada e <strong>${indicacao.nome_indicado}</strong> deixará de constar como
                   indicado(a) por você. O registro permanece no histórico.`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Sim, encerrar",
            cancelButtonText: "Manter indicação",
        });
        if (!escolha.isConfirmed) return;

        try {
            // rota do próprio médico: a de operação exige permissão que ele não tem (dava 403)
            await api.put(`/pessoas/indicacoes-profissionais/minhas/${indicacao.id}/`, {
                motivo_cancelamento: "Encerrada pelo médico na revisão das indicações.",
            });
            await this.carregar();
        } catch (error) {
            console.error("Erro ao encerrar a indicação:", error);
            MySwal.fire({ icon: "error", title: "Atenção!", showCloseButton: true,
                text: "Não foi possível encerrar a indicação. Tente novamente." });
        }
    };

    validar = () => {
        const { form } = this.state;
        const erros = {};
        // obrigatórios conforme o documento; e-mail é o único opcional
        if (!form.nome_indicado.trim()) erros.nome_indicado = "Informe o nome do profissional.";
        if (!form.conselho) erros.conselho = "Informe o conselho.";
        if (!form.registro.trim()) erros.registro = "Informe o número de registro.";
        if (!form.registro_uf) erros.registro_uf = "Informe a UF.";
        if (!String(form.telefone || "").trim()) erros.telefone = "Informe o telefone.";
        if (form.autoriza_contato === null) erros.autoriza_contato = "Responda a autorização de contato.";
        if (!form.assino) erros.assino = "Confirme a leitura e a assinatura da declaração.";
        this.setState({ erros });
        return Object.keys(erros).length === 0;
    };

    enviar = async (e) => {
        e.preventDefault();
        if (!this.validar()) return;

        this.setState({ salvando: true });
        try {
            // `assino` é confirmação de tela: quem registra a assinatura é o backend
            const dados = { ...this.state.form };
            delete dados.assino;
            const { data } = await api.post("/pessoas/indicacoes-profissionais/minhas/", {
                programa_id: this.programaId(),
                ...dados,
            });

            await MySwal.fire({
                icon: "success",
                title: "Indicação enviada",
                html: `Sua indicação foi encaminhada para o programa. Informe ao profissional indicado para entrar
                       em contato para ativar o seu cadastro no telefone <strong>0800 590 0744</strong> ou pelo
                       WhatsApp <strong>(11) 94362-9941</strong>.`,
                confirmButtonText: "Entendi",
                // a declaração assinada é a evidência do ato: fica à mão logo depois de assinar
                showDenyButton: !!data?.declaracao_url,
                denyButtonText: "Ver declaração assinada",
                denyButtonColor: "#6c757d",
            }).then((escolha) => {
                if (escolha.isDenied && data?.declaracao_url) {
                    window.open(data.declaracao_url, "_blank", "noopener");
                }
            });

            this.setState({ salvando: false, form: this.formVazio(), mostrarFormulario: false });
            await this.carregar();
        } catch (error) {
            console.error("Erro ao enviar a indicação:", error);
            this.setState({ salvando: false });
            // o backend explica o motivo (ex.: profissional já indicado e ativo); engolir a
            // mensagem deixava o médico sem saber o que fazer -- e "tente novamente" é
            // conselho errado, porque repetir falha igual
            const dados = error.response?.data || {};
            const detalhe = dados.detail || Object.values(dados).flat()[0];
            MySwal.fire({ icon: "error", title: "Atenção!", showCloseButton: true,
                text: detalhe || "Não foi possível registrar a indicação. Tente novamente." });
        }
    };

    renderStatus = (status) => {
        const mapa = {
            ativa: { cor: "success", texto: "Ativa" },
            cancelada: { cor: "secondary", texto: "Encerrada" },
            rascunho: { cor: "warning", texto: "Aguardando assinatura" },
        };
        const item = mapa[status] || { cor: "light", texto: status };
        return <Badge bg={item.cor}>{item.texto}</Badge>;
    };

    /** Onde a indicação parou: o médico assina a declaração, mas quem aceita o termo é o
     *  profissional indicado -- sem isto ele não sabia se ainda faltava alguma coisa. */
    renderStatusTermo = (i) => {
        if (i.status === "cancelada") return <span className="text-muted small">—</span>;
        if (i.data_aceite_termo) {
            return <Badge bg="success">Aceito em {formataDataHoraExibicao(i.data_aceite_termo)}</Badge>;
        }
        if (!i.dados_indicado) return <Badge bg="info">Aguardando cadastro</Badge>;
        return <Badge bg="warning">Aguardando aceite</Badge>;
    };

    /** Declaração assinada daquela indicação. A URL vem assinada do backend e expira. */
    renderLinkDeclaracao = (indicacao) => {
        if (!indicacao.declaracao_url) return <span className="text-muted small">—</span>;
        return (
            <Button variant="light" size="sm" href={indicacao.declaracao_url}
                    target="_blank" rel="noopener noreferrer">
                <FaFilePdf className="me-1" /> Ver
            </Button>
        );
    };

    renderRevisao = () => {
        const ativas = this.state.indicacoes.filter((i) => i.status === "ativa");
        if (!ativas.length) return null;

        return (
            <Card className="cardDestaque mb-4">
                <Card.Body>
                    <h5>Você já indicou estes profissionais</h5>
                    <p className="text-muted">
                        Antes de indicar alguém novo, confirme se as indicações abaixo devem continuar valendo.
                    </p>

                    <Table responsive className="custom-table">
                        <thead>
                            <tr>
                                <th>Profissional</th>
                                <th>Conselho</th>
                                <th>Indicado em</th>
                                <th>Situação</th>
                                <th>Termo</th>
                                <th>Declaração</th>
                                <th className="text-end">Manter?</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ativas.map((i) => (
                                <tr key={i.id}>
                                    <td>{i.nome_indicado}</td>
                                    <td>{[i.conselho, i.registro, i.registro_uf].filter(Boolean).join(" ") || "-"}</td>
                                    <td>{i.data_indicacao ? formataDataHoraExibicao(i.data_indicacao) : "-"}</td>
                                    <td>{this.renderStatus(i.status)}</td>
                                    <td>{this.renderStatusTermo(i)}</td>
                                    <td>{this.renderLinkDeclaracao(i)}</td>
                                    <td className="text-end text-nowrap">
                                        <Button variant="outline-danger" size="sm" onClick={() => this.encerrarIndicacao(i)}>
                                            <FaTimes className="me-1" /> Encerrar
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>

                    <div className="mt-3">
                        <Button variant="success" onClick={() => this.setState({ mostrarFormulario: true })}>
                            <FaUserPlus className="me-1" /> Indicar outro profissional
                        </Button>
                    </div>
                </Card.Body>
            </Card>
        );
    };

    renderFormulario = () => {
        const { form, erros, salvando, programa } = this.state;

        return (
            <Card className="cardDestaque">
                <Card.Body>
                    <h5>Dados do profissional indicado</h5>
                    <Form onSubmit={this.enviar}>
                        <Row>
                            <Col sm={6} xs={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Nome do(a) profissional indicado(a) <span className="text-danger">*</span></Form.Label>
                                    <Form.Control type="text" name="nome_indicado" value={form.nome_indicado}
                                                  onChange={this.handleChange} isInvalid={!!erros.nome_indicado} />
                                    <Form.Control.Feedback type="invalid">{erros.nome_indicado}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col sm={3} xs={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Profissão / Conselho <span className="text-danger">*</span></Form.Label>
                                    <Form.Select name="conselho" value={form.conselho} onChange={this.handleChange}
                                                 isInvalid={!!erros.conselho}>
                                        <option value="">Selecione</option>
                                        {CONSELHOS.map((c) => <option key={c} value={c}>{c}</option>)}
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">{erros.conselho}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col sm={3} xs={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>UF <span className="text-danger">*</span></Form.Label>
                                    <Form.Select name="registro_uf" value={form.registro_uf} onChange={this.handleChange}
                                                 isInvalid={!!erros.registro_uf}>
                                        <option value="">UF</option>
                                        {UFS.map((u) => <option key={u} value={u}>{u}</option>)}
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">{erros.registro_uf}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col sm={4} xs={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Nº de registro profissional <span className="text-danger">*</span></Form.Label>
                                    <Form.Control type="text" name="registro" value={form.registro}
                                                  onChange={this.handleChange} isInvalid={!!erros.registro} />
                                    <Form.Control.Feedback type="invalid">{erros.registro}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col sm={4} xs={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Telefone <span className="text-danger">*</span></Form.Label>
                                    <Form.Control as={IMaskInput} unmask="typed" mask="(00) 00000-0000"
                                                  name="telefone" value={form.telefone}
                                                  onAccept={this.handleMaskAccept("telefone")}
                                                  isInvalid={!!erros.telefone} />
                                    <Form.Control.Feedback type="invalid">{erros.telefone}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col sm={4} xs={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>E-mail</Form.Label>
                                    <Form.Control type="email" name="email" value={form.email} onChange={this.handleChange} />
                                    <Form.Text muted>Opcional.</Form.Text>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Form.Group className="mb-3">
                            <Form.Label>
                                Autorizo que o Programa de Suporte ao Paciente realize contato diretamente comigo e/ou
                                com o(a) profissional por mim indicado(a), sempre que necessário, para esclarecimento
                                de dúvidas e/ou comunicação de informações urgentes ou emergenciais.*
                            </Form.Label>
                            <div>
                                {[["SIM", true], ["NÃO", false]].map(([rotulo, valor]) => (
                                    <Form.Check inline type="radio" key={rotulo} id={`autoriza_${rotulo}`}
                                                name="autoriza_contato" label={rotulo}
                                                checked={form.autoriza_contato === valor}
                                                onChange={() => this.setState((prev) => ({
                                                    form: { ...prev.form, autoriza_contato: valor },
                                                    erros: { ...prev.erros, autoriza_contato: "" },
                                                }))} />
                                ))}
                            </div>
                            {erros.autoriza_contato && (
                                <div className="text-danger small mt-1">{erros.autoriza_contato}</div>
                            )}
                        </Form.Group>

                        {/* assinar sem poder ler o documento seria assinar no escuro: o PDF do
                            programa abre aqui, e a confirmação só libera depois */}
                        <Alert variant="light" className="border">
                            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                                <div>
                                    <strong>Declaração de indicação</strong>
                                    <div className="text-muted small">
                                        Ao confirmar, você assina eletronicamente a declaração com os dados acima.
                                    </div>
                                </div>
                                {programa?.declaracao_url && (
                                    <Button variant="outline-secondary" size="sm" href={programa.declaracao_url}
                                            target="_blank" rel="noopener noreferrer">
                                        <FaFilePdf className="me-1" /> Ler a declaração
                                    </Button>
                                )}
                            </div>

                            <Form.Check
                                type="checkbox" id="assino_declaracao" className="mt-3"
                                label="Li a declaração e assino eletronicamente as informações que prestei."
                                checked={form.assino}
                                onChange={() => this.setState((prev) => ({
                                    form: { ...prev.form, assino: !prev.form.assino },
                                    erros: { ...prev.erros, assino: "" },
                                }))}
                            />
                            {erros.assino && <div className="text-danger small mt-1">{erros.assino}</div>}
                        </Alert>

                        <Button variant="success" type="submit" className="btn-submit" disabled={salvando}>
                            {salvando ? <span className="spinner-border spinner-border-sm"></span>
                                      : <><FaCheck className="me-1" /> Salvar e assinar declaração</>}
                        </Button>
                        {this.state.indicacoes.some((i) => i.status === "ativa") && (
                            <Button variant="link" className="ms-2"
                                    onClick={() => this.setState({ mostrarFormulario: false })}>
                                Cancelar
                            </Button>
                        )}
                    </Form>
                </Card.Body>
            </Card>
        );
    };

    renderHistorico = () => {
        const encerradas = this.state.indicacoes.filter((i) => i.status !== "ativa");
        if (!encerradas.length) return null;

        return (
            <Card className="cardDestaque mt-4">
                <Card.Body>
                    <h6 className="text-muted">Histórico</h6>
                    <Table responsive className="custom-table mt-2">
                        <thead>
                            <tr>
                                <th>Profissional</th>
                                <th>Conselho</th>
                                <th>Indicado em</th>
                                <th>Situação</th>
                                <th>Termo</th>
                                <th className="text-end">Declaração</th>
                            </tr>
                        </thead>
                        <tbody>
                            {encerradas.map((i) => (
                                <tr key={i.id}>
                                    <td>{i.nome_indicado}</td>
                                    <td>{[i.conselho, i.registro, i.registro_uf].filter(Boolean).join(" ") || "-"}</td>
                                    <td>{i.data_indicacao ? formataDataHoraExibicao(i.data_indicacao) : "-"}</td>
                                    <td>{this.renderStatus(i.status)}</td>
                                    <td>{this.renderStatusTermo(i)}</td>
                                    <td className="text-end">{this.renderLinkDeclaracao(i)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>
        );
    };

    render() {
        const { carregando, erro, mostrarFormulario } = this.state;

        if (carregando) {
            return (
                <div className="text-center mt-5">
                    <Spinner animation="border" role="status"><span className="visually-hidden">Carregando...</span></Spinner>
                    <p className="mt-2">Carregando suas indicações...</p>
                </div>
            );
        }

        if (erro) {
            return (
                <Alert variant="warning" className="mt-4">
                    <FaExclamationTriangle className="me-2" />{erro}
                </Alert>
            );
        }

        return (
            <div>
                <Row className="align-items-center mb-3">
                    <Col sm={8} lg={8}>
                        <h4 className="page-title mb-0">Indicação de Profissional de Saúde</h4>
                        {/* a faixa do topo é escura: `text-muted` some nela (mesmo caso de Feriados) */}
                        <small className="text-white">
                            Indique o profissional que fará o acompanhamento assistencial dos seus pacientes durante o teste.
                        </small>
                    </Col>
                </Row>

                {!mostrarFormulario && this.renderRevisao()}
                {mostrarFormulario && this.renderFormulario()}
                {this.renderHistorico()}
            </div>
        );
    }
}

IndicacoesProfissionais.propTypes = {
    router: PropTypes.object,
};

export default withRouter(IndicacoesProfissionais);
